import { Lang } from '@components/Contexts/LocaleContext';
import { translate2 } from 'utils/translate';

export const EMAIL_PATTERN = /^\S+@\S+\.\S+$/;

// Phone numbers are typed in every shape people are used to — 081-234-5678,
// (02) 123 4567, +66 81 234 5678 — so the separators are stripped before the
// digit count is checked. The value itself is never rewritten: what the user
// typed is what gets stored.
export const PHONE_SEPARATOR_PATTERN = /[\s\-.()]/g;
export const PHONE_PATTERN = /^\+?\d{9,15}$/;

export const isValidEmail = (value: any) =>
  EMAIL_PATTERN.test(String(value ?? '').trim());

export const isValidPhone = (value: any) =>
  PHONE_PATTERN.test(String(value ?? '').replace(PHONE_SEPARATOR_PATTERN, ''));

export const emailMessage = (lang: Lang) =>
  translate2(
    'รูปแบบของ Email ไม่ถูกต้อง กรุณากรอก Email ให้ถูกต้อง',
    'Invalid email format. Please enter a valid email.',
    lang,
  );

export const phoneMessage = (lang: Lang) =>
  translate2(
    'รูปแบบของหมายเลขโทรศัพท์ไม่ถูกต้อง กรุณากรอกหมายเลขโทรศัพท์ให้ถูกต้อง',
    'Invalid phone number format. Please enter a valid phone number.',
    lang,
  );

const EMAIL_FIELDS = ['email'];
const PHONE_FIELDS = ['phone', 'phoneNumber', 'tel'];

/**
 * react-hook-form rules for a field, keyed by its name.
 *
 * Forms are driven by field lists that come from Strapi, so the field name is
 * the only thing available to decide what a value should look like. Returns an
 * empty object for names that need no format check, which makes it safe to
 * spread into every `register()` call.
 *
 * Phone uses `validate` rather than `pattern` because the separators have to be
 * stripped before the digits are counted, which a single regex cannot express.
 */
export const formatRules = (fieldName: string, lang: Lang) => {
  if (EMAIL_FIELDS.includes(fieldName)) {
    return {
      validate: (value: any) =>
        !value || isValidEmail(value) || emailMessage(lang),
    };
  }

  if (PHONE_FIELDS.includes(fieldName)) {
    return {
      validate: (value: any) =>
        !value || isValidPhone(value) || phoneMessage(lang),
    };
  }

  return {};
};

interface RequiredRuleOptions {
  /** "Please enter your <label>" rather than "Please enter <label>". */
  possessive?: boolean;
  /**
   * Lower-case the label. Right for a field label ("Phone Number"), wrong for
   * anything sentence-shaped — some forms pass a placeholder through as the
   * label. No-op in Thai.
   */
  lowercase?: boolean;
}

/**
 * react-hook-form `required` rule with the message every form uses:
 * "กรุณากรอก <label>" / "Please enter your <label>".
 */
export const requiredRule = (
  required: boolean,
  label: string,
  lang: Lang,
  { possessive = true, lowercase = true }: RequiredRuleOptions = {},
) => {
  const text = label ?? '';

  return {
    required: {
      value: required,
      message: `${translate2(
        'กรุณากรอก',
        possessive ? 'Please enter your' : 'Please enter',
        lang,
      )} ${lowercase ? text.toLowerCase() : text}`,
    },
  };
};
